import Highcharts, {
  type ChartPositionObject,
  type Pointer,
  type PositionObject,
  type Tooltip,
} from 'highcharts';

type PointerWithChartPosition = Pointer & {
  chartPosition?: ChartPositionObject;
};

type TooltipWithDistance = Tooltip & {
  distance: number;
};

/**
 * Convert a Highcharts page/document position into coordinates relative to #pageRoot.
 * Matches the coordinate system Highcharts `offset()` uses (getBoundingClientRect + page scroll).
 */
export const adjustPositionByContainer = (positionObj: PositionObject): PositionObject => {
  const pageRoot = document.getElementById('pageRoot');
  if (!pageRoot) {
    return positionObj;
  }

  const docElem = document.documentElement;
  const rootRect = pageRoot.getBoundingClientRect();
  const rootLeft =
    rootRect.left + (window.pageXOffset || docElem.scrollLeft) - (docElem.clientLeft || 0);
  const rootTop =
    rootRect.top + (window.pageYOffset || docElem.scrollTop) - (docElem.clientTop || 0);

  return {
    x: positionObj.x - rootLeft,
    y: positionObj.y - rootTop,
  };
};

const withPageRootChartPosition = (pointer: Pointer, run: () => void) => {
  const pointerWithCache = pointer as PointerWithChartPosition;
  const originalGetChartPosition = pointer.getChartPosition;
  const cachedChartPosition = pointerWithCache.chartPosition;
  pointerWithCache.chartPosition = undefined;

  pointer.getChartPosition = function (this: Pointer): ChartPositionObject {
    const chartPosition = originalGetChartPosition.call(this);
    const adjusted = adjustPositionByContainer({
      x: chartPosition.left,
      y: chartPosition.top,
    });

    return {
      ...chartPosition,
      left: adjusted.x,
      top: adjusted.y,
    };
  };

  try {
    run();
  } finally {
    pointer.getChartPosition = originalGetChartPosition;
    pointerWithCache.chartPosition = cachedChartPosition;
  }
};

/**
 * By default, Highcharts places the tooltip container at the end of `body`. This function moves it
 * to the #pageRoot element instead so it is inside the scrollable main content area. Without this
 * override, the tooltip will stay in a fixed position when the page is scrolled.
 */
Highcharts.wrap(Highcharts.Tooltip.prototype, 'getLabel', function (this: Tooltip, proceed) {
  // eslint-disable-next-line prefer-rest-params
  const result = proceed.apply(this, Array.prototype.slice.call(arguments, 1));

  // Add the tooltip container to the #pageRoot element instead of `body`.
  if (this.container?.parentElement === document.body) {
    document.getElementById('pageRoot')?.appendChild(this.container);
  }
  return result;
});

/**
 * Outside-tooltip overflow math must use the same coordinate space as the remounted container.
 * Pair pageRoot-sized playing field with pageRoot-relative chart offsets during updatePosition.
 */
Highcharts.wrap(Highcharts.Tooltip.prototype, 'getPlayingField', function (this: Tooltip, proceed) {
  if (!this.outside) {
    // eslint-disable-next-line prefer-rest-params
    return proceed.apply(this, Array.prototype.slice.call(arguments, 1));
  }

  const pageRoot = document.getElementById('pageRoot');
  if (!pageRoot) {
    // eslint-disable-next-line prefer-rest-params
    return proceed.apply(this, Array.prototype.slice.call(arguments, 1));
  }

  const distance = (this as TooltipWithDistance).distance;
  return {
    width: Math.max(0, pageRoot.clientWidth - 2 * distance - 2),
    height: Math.max(0, pageRoot.scrollHeight - 2 * distance),
  };
});

Highcharts.wrap(Highcharts.Tooltip.prototype, 'updatePosition', function (this: Tooltip, proceed) {
  // eslint-disable-next-line prefer-rest-params
  const args = Array.prototype.slice.call(arguments, 1);
  withPageRootChartPosition(this.chart.pointer, () => {
    proceed.apply(this, args);
  });
});
